/* =============================================================================
 * Copyright (c) 2026 SKYXOPS Corp. All rights reserved.
 *
 * This file is proprietary and confidential. Unauthorized copying,
 * distribution, modification, or use of this file, via any medium,
 * is strictly prohibited without the express written permission of
 * SKYXOPS Corp.
 * ============================================================================= */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Input,
} from '@skyxops/ui';
import { authOrigin } from '../authOrigin';
import {
  ApiError,
  confirmForgotPassword,
  forgotPassword,
  login,
  scrubUrlParams,
  setToken,
} from '../api';

interface Props {
  onAuthed: () => void;
}

const SKYXOPS_LOGO = 'https://appstatic.skyxops.io/skyxops/SKYXOPS-Logo.png';
const GOOGLE_ICON = 'https://appstatic.skyxops.io/login-icons/Google.png';
const MICROSOFT_ICON = 'https://appstatic.skyxops.io/login-icons/Microsoft.png';

type View = 'login' | 'mfa' | 'forgot' | 'sent' | 'reset';

export function LoginPage({ onAuthed }: Props) {
  const navigate = useNavigate();
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string>) =>
    intl.formatMessage({ id }, values);
  const [view, setView] = useState<View>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaSession, setMfaSession] = useState<string | null>(null);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Arriving via the emailed reset link (/reset?email=&code=) → jump to the
  // reset form with the code captured from the URL (the form keys off it).
  // Also surface SSO callback errors (/login?error=…), most importantly
  // no_account → "you don't have an account, contact SKYXOPS".
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const e = sp.get('email');
    const c = sp.get('code');
    if (e || c) {
      if (e) setEmail(e);
      if (c) setResetCode(c);
      setView('reset');
      // AI-LAUNCH-45: the one-time reset code (and the address it belongs to)
      // must not linger in the address bar or browser history once captured.
      scrubUrlParams('email', 'code');
    }
    const err = sp.get('error');
    if (err) {
      const KNOWN_SSO_ERRORS = [
        'no_account', 'email_unverified', 'email_mismatch', 'invite_invalid',
        'sso_failed', 'sso_state', 'sso_unconfigured',
      ];
      setError(KNOWN_SSO_ERRORS.includes(err)
        ? t(`login.sso_err_${err}`)
        : t('login.sso_err_generic'));
      // Strip the error param so it doesn't persist on refresh.
      scrubUrlParams('error');
    }
  }, []);

  function resetState(next: View) {
    // Clears messages only — used for the MFA hop, which must keep email/password
    // so the challenge can be submitted.
    setError(null);
    setNotice(null);
    setView(next);
  }

  // Explicit navigation (Forgot password? / Back to sign in) — start the target
  // view with a clean form so nothing carries over from the previous one.
  function goTo(next: View) {
    setError(null);
    setNotice(null);
    setEmail('');
    setPassword('');
    setMfaCode('');
    setMfaSession(null);
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setView(next);
  }

  function describe(err: unknown, fallback: string): string {
    if (err instanceof ApiError && err.status === 401) return t('login.error_invalid_credentials');
    if (err instanceof ApiError && err.message) return err.message;
    return err instanceof Error ? err.message : fallback;
  }

  async function onLogin(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const resp = await login(email.trim(), password, {
        mfaCode: view === 'mfa' ? mfaCode.trim() : undefined,
        session: view === 'mfa' ? mfaSession ?? undefined : undefined,
      });
      if (resp.mfa_required && resp.session) {
        setMfaSession(resp.session);
        resetState('mfa');
        return;
      }
      if (resp.token) setToken(resp.token);
      onAuthed();
      navigate('/', { replace: true });
    } catch (err) {
      setError(describe(err, view === 'mfa'
        ? t('login.error_invalid_code') : t('login.error_signin_failed')));
    } finally {
      setBusy(false);
    }
  }

  async function onForgot(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await forgotPassword(email.trim());
      setView('sent');
    } catch (err) {
      // AI-AUTH-114: auth-svc no longer answers 404 for an unknown address — every
      // user-specific outcome is the same 202, so the form cannot be used to test
      // whether an account exists. The branch that rendered "No account found for
      // that email." here was the visible half of that oracle and is gone with it.
      setError(describe(err, t('login.error_reset_start_failed')));
    } finally {
      setBusy(false);
    }
  }

  async function onReset(e: React.FormEvent) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError(t('login.error_password_mismatch'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await confirmForgotPassword(email.trim(), resetCode.trim(), newPassword);
      setPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setResetCode('');
      resetState('login');
      setNotice(t('login.notice_password_updated'));
    } catch (err) {
      setError(describe(err, t('login.error_reset_failed')));
    } finally {
      setBusy(false);
    }
  }

  const titles: Record<View, { title: string; subtitle: string }> = {
    login: { title: t('login.title'), subtitle: t('login.subtitle') },
    mfa: { title: t('login.mfa_title'), subtitle: t('login.mfa_subtitle') },
    forgot: { title: t('login.forgot_title'), subtitle: t('login.forgot_subtitle') },
    sent: { title: t('login.sent_title'), subtitle: '' },
    reset: { title: t('login.reset_title'), subtitle: t('login.reset_subtitle') },
  };

  const fieldLabel = 'text-sm font-medium text-foreground';

  function startSso(provider: 'Google' | 'Microsoft') {
    // auth-svc owns the OAuth flow (state + PKCE + nonce). Its origin is
    // configuration, not a rewrite of the current hostname (AI-LAUNCH-40).
    const origin = authOrigin();
    if (!origin) {
      setError(t('login.error_sso_unconfigured_site'));
      return;
    }
    window.location.href = `${origin}/api/v1/auth/sso/start?provider=${provider.toLowerCase()}`;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-violet-50 p-4 dark:from-skyxops-deepMidnight dark:via-skyxops-midnight dark:to-skyxops-ink">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-border/60 bg-card p-8 shadow-xl">
          <div className="mb-7 flex justify-center">
            <img src={SKYXOPS_LOGO} alt="SKYXOPS" className="h-9 w-auto" />
          </div>
          {view !== 'login' && (
            <div className="mb-6 space-y-1 text-center">
              <h1 className="text-xl font-semibold tracking-tight text-foreground">{titles[view].title}</h1>
              <p className="text-sm text-muted-foreground">{titles[view].subtitle}</p>
            </div>
          )}

          {notice && (
            <Alert className="mb-4 border-skyxops-beach/40 bg-skyxops-beach/10">
              <AlertDescription className="text-sm">{notice}</AlertDescription>
            </Alert>
          )}
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertTitle><FormattedMessage id="login.error_title" /></AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* ---- LOGIN ---- */}
          {view === 'login' && (
            <>
              <div className="space-y-2">
                <button type="button" onClick={() => startSso('Google')} disabled={busy}
                  className="flex w-full items-center justify-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition hover:bg-muted disabled:opacity-50">
                  <img src={GOOGLE_ICON} alt="" className="h-4 w-4" /> <FormattedMessage id="login.sso_google" />
                </button>
                <button type="button" onClick={() => startSso('Microsoft')} disabled={busy}
                  className="flex w-full items-center justify-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition hover:bg-muted disabled:opacity-50">
                  <img src={MICROSOFT_ICON} alt="" className="h-4 w-4" /> <FormattedMessage id="login.sso_microsoft" />
                </button>
              </div>
              <div className="my-5 flex items-center gap-3">
                <span className="h-px flex-1 bg-border" />
                <span className="text-xs uppercase tracking-wide text-muted-foreground"><FormattedMessage id="login.divider_or" /></span>
                <span className="h-px flex-1 bg-border" />
              </div>
            <form onSubmit={onLogin} className="space-y-4">
              <div className="space-y-1.5">
                <label htmlFor="email" className={fieldLabel}><FormattedMessage id="login.label_email" /></label>
                <Input id="email" type="email" placeholder={t('login.placeholder_email')} autoComplete="username"
                  value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} required autoFocus />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="password" className={fieldLabel}><FormattedMessage id="login.label_password" /></label>
                <Input id="password" type="password" placeholder="••••••••" autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)} disabled={busy} required />
                <div className="flex justify-end pt-1">
                  <button type="button" onClick={() => goTo('forgot')}
                    className="text-xs font-medium text-skyxops-lilac hover:underline">
                    <FormattedMessage id="login.forgot_link" />
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full bg-skyxops-midnight text-white hover:bg-skyxops-midnight/90"
                disabled={busy || !email || !password}>
                {busy ? t('login.submitting') : t('login.submit')}
              </Button>
            </form>
            <p className="mt-5 text-center text-xs text-muted-foreground">
              <FormattedMessage id="login.new_to_skyxops" />{' '}
              <a
                href="https://www.skyxops.com/contact-us"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-skyxops-lilac hover:underline"
              >
                <FormattedMessage id="login.contact_us" />
              </a>
            </p>
            </>
          )}

          {/* ---- MFA ---- */}
          {view === 'mfa' && (
            <form onSubmit={onLogin} className="space-y-4">
              <div className="space-y-1.5">
                <label htmlFor="mfa" className={fieldLabel}><FormattedMessage id="login.label_mfa_code" /></label>
                <Input id="mfa" inputMode="numeric" autoComplete="one-time-code" placeholder="123456"
                  className="text-center text-lg tracking-[0.5em]" maxLength={6}
                  value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                  disabled={busy} required autoFocus />
              </div>
              <Button type="submit" className="w-full bg-skyxops-midnight text-white hover:bg-skyxops-midnight/90"
                disabled={busy || mfaCode.length < 6}>
                {busy ? t('login.mfa_submitting') : t('login.mfa_submit')}
              </Button>
              <button type="button" onClick={() => goTo('login')}
                className="w-full text-center text-xs text-muted-foreground hover:underline">
                <FormattedMessage id="login.back_to_signin" />
              </button>
            </form>
          )}

          {/* ---- FORGOT ---- */}
          {view === 'forgot' && (
            <form onSubmit={onForgot} className="space-y-4">
              <div className="space-y-1.5">
                <label htmlFor="fp-email" className={fieldLabel}><FormattedMessage id="login.label_email" /></label>
                <Input id="fp-email" type="email" placeholder={t('login.placeholder_email')} autoComplete="username"
                  value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} required autoFocus />
              </div>
              <Button type="submit" className="w-full bg-skyxops-midnight text-white hover:bg-skyxops-midnight/90"
                disabled={busy || !email}>
                {busy ? t('login.forgot_submitting') : t('login.forgot_submit')}
              </Button>
              <button type="button" onClick={() => goTo('login')}
                className="w-full text-center text-xs text-muted-foreground hover:underline">
                <FormattedMessage id="login.back_to_signin" />
              </button>
            </form>
          )}

          {/* ---- SENT (link emailed) ---- */}
          {view === 'sent' && (
            <div className="space-y-5 text-center">
              <p className="text-sm text-muted-foreground">
                <FormattedMessage
                  id="login.sent_body"
                  values={{ email: <span className="font-medium text-foreground">{email.trim()}</span> }}
                />
              </p>
              <p className="text-xs text-muted-foreground">
                <FormattedMessage
                  id="login.sent_retry"
                  values={{
                    retry: (
                      <button type="button" onClick={() => goTo('forgot')} className="font-medium text-skyxops-lilac hover:underline">
                        <FormattedMessage id="login.sent_retry_link" />
                      </button>
                    ),
                  }}
                />
              </p>
              <button type="button" onClick={() => goTo('login')}
                className="w-full text-center text-xs text-muted-foreground hover:underline">
                <FormattedMessage id="login.back_to_signin" />
              </button>
            </div>
          )}

          {/* ---- RESET (only reached via the emailed link) ---- */}
          {view === 'reset' && !resetCode && (
            <div className="space-y-5 text-center">
              <p className="text-sm text-muted-foreground">
                <FormattedMessage id="login.reset_invalid" />
              </p>
              <button type="button" onClick={() => goTo('forgot')}
                className="w-full bg-skyxops-midnight text-white hover:bg-skyxops-midnight/90 rounded-md py-2 text-sm font-medium">
                <FormattedMessage id="login.reset_request_new" />
              </button>
            </div>
          )}
          {view === 'reset' && resetCode && (
            <form onSubmit={onReset} className="space-y-4">
              <div className="space-y-1.5">
                <label htmlFor="np" className={fieldLabel}><FormattedMessage id="login.label_new_password" /></label>
                <Input id="np" type="password" placeholder="••••••••" autoComplete="new-password"
                  value={newPassword} onChange={(e) => setNewPassword(e.target.value)} disabled={busy} required />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="cp" className={fieldLabel}><FormattedMessage id="login.label_confirm_password" /></label>
                <Input id="cp" type="password" placeholder="••••••••" autoComplete="new-password"
                  value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} disabled={busy} required />
              </div>
              <p className="text-xs text-muted-foreground">
                <FormattedMessage id="login.password_policy" />
              </p>
              <Button type="submit" className="w-full bg-skyxops-midnight text-white hover:bg-skyxops-midnight/90"
                disabled={busy || !resetCode || !newPassword || !confirmPassword}>
                {busy ? t('login.reset_submitting') : t('login.reset_submit')}
              </Button>
              <button type="button" onClick={() => goTo('login')}
                className="w-full text-center text-xs text-muted-foreground hover:underline">
                <FormattedMessage id="login.back_to_signin" />
              </button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          <FormattedMessage id="login.footer" values={{ year: '2026' }} />
        </p>
      </div>
    </div>
  );
}
